// aluno/frequencia.js — tela de frequência do aluno (minha frequência).
//
// Origem (monólito): linhas 6270-6313 (HTML frequenciaScreen),
//   5433-5452 (showFrequencia), 5323-5431 (renderFrequencia).
//
// Dependências:
//   - api    (core/api.js)    — api.get('frequencia')
//   - auth   (core/auth.js)   — auth.getCurrent()
//   - notify (core/notify.js) — notify.error, notify.loading
//   - router (core/router.js) — router.go('/aluno')
//
// Formato da API (action=frequencia):
//   Objeto indexado por nome normalizado do aluno:
//   {
//     "NOME ALUNO": {
//       nome: string,
//       turma: string,
//       cpf: string,          // 11 dígitos sem pontuação
//       registros: [
//         { data: string, modulo: string, materia: string, status: string }
//       ]
//     }
//   }
//   Filtragem: comparar cpf === auth.getCurrent().senha
//
// Exporta:
//   function render(container)

import { api }    from '../core/api.js';
import { auth }   from '../core/auth.js';
import { notify } from '../core/notify.js';
import { router } from '../core/router.js';
import { skelBlock, skelList } from '../core/skeletons.js';

// ── Constantes ───────────────────────────────────────────────────────────────

const MESES_ABREV  = ['','JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];
const DIAS_SEMANA  = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Converte "DD/MM/YYYY" ou "YYYY-MM-DD" em objeto Date.
 * @param {string} dataStr
 * @returns {Date|null}
 */
function _parseData(dataStr) {
  if (!dataStr) return null;
  // Formato ISO: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dataStr)) {
    const [a, m, d] = dataStr.split('-').map(Number);
    return new Date(a, m - 1, d);
  }
  // Formato BR: DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataStr)) {
    const [d, m, a] = dataStr.split('/').map(Number);
    return new Date(a, m - 1, d);
  }
  return null;
}

/**
 * Formata data para exibição: { diaN, mesAbrev, diaSemana }.
 * @param {Date} dateObj
 */
function _formatarData(dateObj) {
  const mes = dateObj.getMonth() + 1;
  return {
    diaN:      dateObj.getDate(),
    mesAbrev:  MESES_ABREV[mes] || '—',
    diaSemana: DIAS_SEMANA[dateObj.getDay()] || '—',
  };
}

/**
 * Processa a lista de registros do aluno para exibição.
 * Ordena por data ascendente.
 * @param {Array<{data,modulo,materia,status}>} registros
 * @returns {Array<object>}
 */
function _processarRegistros(registros) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return registros
    .map(r => {
      const dateObj = _parseData(r.data);
      if (!dateObj) return null;
      const isPast = dateObj <= hoje;
      const { diaN, mesAbrev, diaSemana } = _formatarData(dateObj);
      return { ...r, dateObj, diaN, mesAbrev, diaSemana, isPast };
    })
    .filter(Boolean)
    .sort((a, b) => a.dateObj - b.dateObj);
}

/**
 * Calcula o resumo (presenças, faltas, percentual) a partir dos registros passados.
 * @param {Array<object>} registros - já processados (com isPast)
 */
function _calcularResumo(registros) {
  let presencas = 0, faltas = 0, justificadas = 0, aguardando = 0;

  registros.forEach(r => {
    if (!r.isPast) { aguardando++; return; }
    if (r.status === 'presente')    presencas++;
    else if (r.status === 'falta')  faltas++;
    else if (r.status === 'justificado') justificadas++;
    else aguardando++;
  });

  const aulasPassadas = presencas + faltas + justificadas;
  const total = aulasPassadas + aguardando;
  const percentual = aulasPassadas > 0 ? Math.round((presencas / aulasPassadas) * 100) : null;

  return { presencas, faltas, justificadas, aguardando, aulasPassadas, total, percentual };
}

// ── Renderizadores parciais ───────────────────────────────────────────────────

/**
 * Renderiza um item de frequência na lista.
 * @param {object} r - registro processado
 * @returns {string} HTML
 */
function _htmlItem(r) {
  let badgeIcon, badgeClass, statusTexto, statusCor;

  switch (r.status) {
    case 'presente':
      badgeIcon  = '✅'; badgeClass = 'presente';
      statusTexto = r.origem === 'formulario' ? 'Confirmado via formulário'
                  : r.origem === 'manual'     ? 'Alterado pelo Admin'
                  : 'Presente';
      statusCor  = '#34c759'; break;
    case 'falta':
      badgeIcon  = '❌'; badgeClass = 'falta';
      statusTexto = r.origem === 'manual' ? 'Falta (alterado pelo Admin)' : 'Falta';
      statusCor  = '#ff3b30'; break;
    case 'justificado':
      badgeIcon  = '📋'; badgeClass = 'justificado';
      statusTexto = 'Falta justificada';
      statusCor  = '#ff9500'; break;
    default:
      badgeIcon  = '📅'; badgeClass = 'futuro';
      statusTexto = 'Agendado';
      statusCor  = '#9B9B9B';
  }

  const disciplina = r.materia || 'Aula';
  const turno      = r.modulo || '';
  const iniciais   = (r.professor || '?').split(' ').slice(0, 2).map(n => n[0]).join('') || '?';

  return `
    <div class="freq-item">
      <div class="freq-item-date">
        <div class="freq-item-day">${r.diaN}</div>
        <div class="freq-item-month">${r.mesAbrev}</div>
      </div>
      <div class="freq-item-divider"></div>
      <div class="freq-prof-avatar" style="background:linear-gradient(135deg,#6A001F,#B3002D)">${iniciais}</div>
      <div class="freq-item-info">
        <div class="freq-item-name">${disciplina}</div>
        <div class="freq-item-sub" style="color:#6A001F;font-weight:600">${r.professor || 'Professor a definir'}</div>
        <div class="freq-item-sub">${r.diaSemana}${turno ? ' · ' + turno : ''}</div>
        ${r.isPast ? `<div style="font-size:10px;font-weight:700;color:${statusCor};margin-top:4px">${statusTexto}</div>` : ''}
      </div>
      <div class="freq-badge ${badgeClass}">${badgeIcon}</div>
    </div>`;
}

/**
 * Renderiza o estado de carregamento.
 * @param {HTMLElement} container
 */
function _mostrarCarregando(container) {
  const main = container.querySelector('#freq-main');
  if (!main) return;
  // Skeleton estrutural: 2 cards de resumo + 4 itens de histórico,
  // ocupando o mesmo espaço aproximado do conteúdo real.
  main.setAttribute('aria-busy', 'true');
  main.innerHTML = `
    <div style="display:flex;gap:10px;margin-bottom:18px">
      ${skelBlock(90, 'flex:1;border-radius:14px')}
      ${skelBlock(90, 'flex:1;border-radius:14px')}
    </div>
    ${skelBlock(28, 'width:60%;margin-bottom:10px')}
    ${skelList({ rows: 4, rowHeight: 62, gap: 10 })}`;
}

/**
 * Renderiza estado de erro.
 * @param {HTMLElement} container
 * @param {string} msg
 */
function _mostrarErro(container, msg) {
  const main = container.querySelector('#freq-main');
  if (main) main.innerHTML = `
    <div style="text-align:center;padding:60px 20px;color:#ff3b30">
      <div style="font-size:32px;margin-bottom:12px">⚠️</div>
      <div style="font-size:14px;font-weight:700">${msg}</div>
      <button id="freq-retry-btn" style="margin-top:20px;padding:12px 24px;background:#6A001F;color:#fff;border:none;border-radius:12px;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;cursor:pointer">
        Tentar novamente
      </button>
    </div>`;
  const retryBtn = container.querySelector('#freq-retry-btn');
  if (retryBtn) retryBtn.addEventListener('click', () => _carregarEExibir(container));
}

/**
 * Popula os elementos de resumo e lista com os dados do aluno.
 * @param {HTMLElement} container
 * @param {object} resumo
 * @param {Array} registrosProcessados
 */
function _renderizarDados(container, resumo, registrosProcessados) {
  // Sumário numérico
  const elPresencas = container.querySelector('#freq-presencas');
  const elFaltas    = container.querySelector('#freq-faltas');
  const elTotal     = container.querySelector('#freq-total');
  const elPct       = container.querySelector('#freq-pct');
  const elBar       = container.querySelector('#freq-bar');
  const elTitulo    = container.querySelector('#freq-section-title');
  const elList      = container.querySelector('#freq-list');

  if (elPresencas) elPresencas.textContent = resumo.presencas;
  if (elFaltas)    elFaltas.textContent    = resumo.faltas;
  if (elTotal)     elTotal.textContent     = resumo.total;

  if (elPct) {
    if (resumo.percentual !== null) {
      elPct.textContent = resumo.percentual + '%';
      elPct.style.color = resumo.percentual >= 75 ? '#34c759'
                        : resumo.percentual > 0   ? '#ff9500'
                        : '#6A001F';
    } else {
      elPct.textContent = '—';
      elPct.style.color = '#6A001F';
    }
  }

  // Barra de progresso (com pequeno delay para animar)
  setTimeout(() => {
    if (elBar) elBar.style.width = (resumo.percentual || 0) + '%';
  }, 100);

  if (elTitulo) {
    elTitulo.textContent = `MBO Pós 2026 — ${resumo.total} Aulas`;
  }

  // Lista de registros
  if (elList) {
    if (!registrosProcessados.length) {
      elList.innerHTML = `
        <div style="text-align:center;padding:40px 20px;color:#9B9B9B">
          <div style="font-size:28px;margin-bottom:12px">📅</div>
          <div style="font-size:13px;font-weight:700">Nenhuma aula registrada ainda.</div>
        </div>`;
    } else {
      elList.innerHTML = registrosProcessados.map(_htmlItem).join('');
    }
  }
}

// ── Carregamento e exibição de dados ─────────────────────────────────────────

/**
 * Busca a frequência da API e popula a tela.
 * @param {HTMLElement} container
 */
async function _carregarEExibir(container) {
  const user = auth.getCurrent();
  if (!user || user.turma === 'Admin') {
    notify.error('Acesso restrito a alunos.');
    router.go('/login');
    return;
  }

  _mostrarCarregando(container);

  const fecharLoading = notify.loading('Carregando frequência...');

  try {
    const dados = await api.get('frequencia');
    fecharLoading();

    // Filtrar pelo CPF do aluno logado (senha = CPF)
    const cpfAluno = user.senha;
    let registrosAluno = [];

    if (dados && typeof dados === 'object') {
      const entrada = Object.values(dados).find(v => v && v.cpf === cpfAluno);
      if (entrada && Array.isArray(entrada.registros)) {
        registrosAluno = entrada.registros;
      }
    }

    const registrosProcessados = _processarRegistros(registrosAluno);
    const resumo = _calcularResumo(registrosProcessados);

    // Restaura o skeleton da tela antes de popular
    const main = container.querySelector('#freq-main');
    if (main) {
      main.innerHTML = `
        <div class="freq-summary">
          <div class="freq-stat presencas">
            <div class="freq-stat-number" id="freq-presencas">0</div>
            <div class="freq-stat-label">Presenças</div>
          </div>
          <div class="freq-stat faltas">
            <div class="freq-stat-number" id="freq-faltas">0</div>
            <div class="freq-stat-label">Faltas</div>
          </div>
          <div class="freq-stat total">
            <div class="freq-stat-number" id="freq-total">0</div>
            <div class="freq-stat-label">Total</div>
          </div>
        </div>
        <div class="freq-progress-wrap">
          <div class="freq-progress-label">
            <span>Frequência</span>
            <span class="freq-progress-pct" id="freq-pct">—</span>
          </div>
          <div class="freq-progress-bar">
            <div class="freq-progress-fill" id="freq-bar" style="width:0%"></div>
          </div>
        </div>
        <div class="freq-section-title" id="freq-section-title">MBO Pós 2026</div>
        <div class="freq-list" id="freq-list"></div>
      `;
    }

    _renderizarDados(container, resumo, registrosProcessados);

  } catch (err) {
    fecharLoading();
    notify.error('Falha ao carregar frequência. Tente novamente.');
    console.error('[frequencia] erro ao carregar:', err);
    _mostrarErro(container, 'Não foi possível carregar sua frequência.');
  }
}

// ── Função principal exportada ────────────────────────────────────────────────

/**
 * Renderiza a tela de frequência do aluno dentro de `container`.
 * Chamado pelo router para a rota #/aluno/frequencia.
 *
 * @param {HTMLElement} container - elemento #app
 */
export function render(container) {
  container.innerHTML = `
    <div id="freq-root">

      <!-- Header -->
      <header class="freq-header">
        <div class="freq-header-inner">
          <button id="freq-back-btn" class="freq-back-btn" type="button" aria-label="Voltar">←</button>
          <div class="freq-header-title">Minha Frequência</div>
        </div>
      </header>

      <!-- Main: preenchido dinamicamente após carregamento da API -->
      <main class="freq-main" id="freq-main">
        <div style="text-align:center;padding:60px 20px;color:#9B9B9B">
          <div style="font-size:32px;margin-bottom:12px">⏳</div>
          <div style="font-size:14px;font-weight:700">Carregando frequência...</div>
        </div>
      </main>

    </div>
  `;

  // Botão voltar
  container.querySelector('#freq-back-btn').addEventListener('click', () => {
    router.go('/aluno');
  });

  // Inicia carregamento
  _carregarEExibir(container);
}
